import React, { useState, useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { useSuppressFloatingCta } from './floatingCtaContext';
// Authentic full-color brand mark with a robust fallback chain so a tile is
// NEVER blank/broken:
//   1. full-color dashboard-icons (walkxcode)  ->
//   2. simpleicons                             ->
//   3. a brand-tinted monogram chip
type Brand = {
  // dashboard-icons slug (hyphenated where needed), e.g. "microsoft-teams"
  iconSlug: string;
  // simpleicons slug fallback, e.g. "microsoftteams"
  simpleSlug: string;
  name: string;
  // monogram fallback styling
  mono: string;
  brandColor: string;
};
const BRANDS: Record<string, Brand> = {
  slack: {
    iconSlug: 'slack',
    simpleSlug: 'slack',
    name: 'Slack',
    mono: 'S',
    brandColor: '#611f69'
  },
  teams: {
    iconSlug: 'microsoft-teams',
    simpleSlug: 'microsoftteams',
    name: 'Teams',
    mono: 'T',
    brandColor: '#4b53bc'
  },
  hubspot: {
    iconSlug: 'hubspot',
    simpleSlug: 'hubspot',
    name: 'HubSpot',
    mono: 'H',
    brandColor: '#ff7a59'
  },
  notion: {
    iconSlug: 'notion',
    simpleSlug: 'notion',
    name: 'Notion',
    mono: 'N',
    brandColor: '#2B211A'
  },
  salesforce: {
    iconSlug: 'salesforce',
    simpleSlug: 'salesforce',
    name: 'Salesforce',
    mono: 'S',
    brandColor: '#00A1E0'
  },
  linear: {
    iconSlug: 'linear',
    simpleSlug: 'linear',
    name: 'Linear',
    mono: 'L',
    brandColor: '#5E6AD2'
  }
};
function BrandLogo({
  brand,
  className = 'w-5 h-5'
}: {
  brand: keyof typeof BRANDS;
  className?: string;
}) {
  const b = BRANDS[brand];
  // 0 = dashboard-icons, 1 = simpleicons, 2 = monogram
  const [stage, setStage] = useState(0);
  if (stage >= 2) {
    return <span className={`flex items-center justify-center rounded-md text-white font-bold ${className}`} style={{
      backgroundColor: b.brandColor
    }} aria-hidden="true" data-id="element-1183">
        <span className="text-[10px] leading-none" data-id="element-1184">{b.mono}</span>
      </span>;
  }
  const src = stage === 0 ? `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${b.iconSlug}.svg` : `https://cdn.simpleicons.org/${b.simpleSlug}`;
  return <img key={stage} src={src} alt={b.name} className={`${className} object-contain`} loading="lazy" onError={() => setStage(s => s + 1)} data-id="element-1185" />;
}
export function GettingStartedSteps() {
  // While this section's own "Get started for free" CTA is on screen, suppress
  // the global floating "Try Giga for free" button so the two don't stack /
  // overlap on mobile.
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaInView = useInView(ctaRef, {
    margin: '0px 0px -10% 0px'
  });
  useSuppressFloatingCta(ctaInView);
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 overflow-hidden" data-id="element-1186">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-1187">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20" data-id="element-1188">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-1189">
            Plug-and-play
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-1190">
            Up and running in 2 minutes
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-1191">
            No setup project. No IT ticket. No training videos.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20" data-id="element-1192">
          {/* Card 1 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5
        }} className="group flex flex-col bg-white rounded-2xl md:rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1193">
            {/* Visual */}
            <div className="relative h-60 sm:h-64 bg-gradient-to-b from-[#1E1B26] to-[#0C0B10] p-6 sm:p-8 flex flex-col items-center justify-center gap-3 sm:gap-3.5 overflow-hidden" data-id="element-1194">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(234,88,12,0.18),transparent_55%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1195" />
              {/* Dotted halftone pattern, anchored to the TOP of the dark header
               and faded out toward the mockup so it reads as texture, not
               noise behind the content. */}
              <div aria-hidden="true" className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent_75%)]" data-id="element-1196" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1197" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1198" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1199" />

              {/* Slack — liquid-glass connection pill. The platform name speaks
               for itself; a green "connected" check implies the link without
               wordy labels. */}
              <div className="relative z-10 w-full max-w-[240px] rounded-2xl p-3 flex items-center justify-between bg-white/10 backdrop-blur-xl ring-1 ring-white/20 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.35)] transform group-hover:-translate-y-1 transition-transform duration-500" data-id="element-1200">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-2xl bg-gradient-to-b from-white/15 to-transparent" data-id="element-1201" />
                <div className="relative flex items-center gap-3" data-id="element-1202">
                  <div className="w-9 h-9 rounded-xl bg-white/85 backdrop-blur flex items-center justify-center shrink-0 ring-1 ring-white/40 shadow-sm" data-id="element-1203">
                    <BrandLogo brand="slack" className="w-5 h-5" data-id="element-1204" />
                  </div>
                  <span className="text-sm font-bold text-white" data-id="element-1205">Slack</span>
                </div>
                <div className="relative flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-emerald-400/20 ring-1 ring-emerald-300/40" data-id="element-1206">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-200" data-id="element-1207">
                    Connected
                  </span>
                  <span className="w-4 h-4 rounded-full bg-emerald-400 flex items-center justify-center text-[#0E0B08]" data-id="element-1208">
                    <Check size={10} strokeWidth={3.5} data-id="element-1209" />
                  </span>
                </div>
              </div>

              <span className="relative z-10 text-white/35 text-[10px] font-semibold uppercase tracking-[0.25em]" data-id="element-1210">
                or
              </span>

              {/* Teams — same liquid-glass pill, but dimmed and tagged "Soon"
               since it isn't live yet. */}
              <div className="relative z-10 w-full max-w-[240px] rounded-2xl p-3 flex items-center justify-between bg-white/[0.06] backdrop-blur-xl ring-1 ring-white/15 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.2)] transform group-hover:translate-y-1 transition-transform duration-500" data-id="element-1211">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-2xl bg-gradient-to-b from-white/10 to-transparent" data-id="element-1212" />
                <div className="relative flex items-center gap-3" data-id="element-1213">
                  <div className="w-9 h-9 rounded-xl bg-white/70 backdrop-blur flex items-center justify-center shrink-0 ring-1 ring-white/30 shadow-sm" data-id="element-1214">
                    <BrandLogo brand="teams" className="w-5 h-5" data-id="element-1215" />
                  </div>
                  <span className="text-sm font-bold text-white/80" data-id="element-1216">Teams</span>
                </div>
                <span className="relative text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-white/10 text-white/50 ring-1 ring-white/15" data-id="element-1217">
                  Soon
                </span>
              </div>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1218">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1219">
                Step 01
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-1220">
                Add to Slack or Teams
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1221">
                Install Giga from the app directory. It shows up like any new
                teammate, ready to talk.
              </p>
            </div>
          </motion.div>

          {/* Card 2 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="group flex flex-col bg-white rounded-2xl md:rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1222">
            {/* Visual */}
            <div className="relative h-60 sm:h-64 bg-gradient-to-b from-[#1E1B26] to-[#0C0B10] flex items-center justify-center overflow-hidden" data-id="element-1223">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(234,88,12,0.2),transparent_70%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1224" />
              {/* Dotted halftone pattern, anchored to the TOP of the dark header
               and faded out toward the mockup so it reads as texture, not
               noise behind the content. */}
              <div aria-hidden="true" className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent_75%)]" data-id="element-1225" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1226" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1227" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1228" />

              {/* Concentric guide rings */}
              <div className="absolute w-[210px] h-[210px] rounded-full border border-white/[0.06]" data-id="element-1229" />
              <div className="absolute w-[150px] h-[150px] rounded-full border border-white/[0.08]" data-id="element-1230" />

              {/* Connective lines from each tool to the core */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 300 256" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden="true" data-id="element-1231">
                {[[78, 64], [60, 184], [222, 72], [232, 200]].map(([x, y], i) => <line key={i} x1="150" y1="128" x2={x} y2={y} stroke="url(#gigaLine)" strokeWidth="1.5" strokeDasharray="3 4" data-id="element-1232" />)}
                <defs data-id="element-1233">
                  <linearGradient id="gigaLine" x1="0" y1="0" x2="1" y2="0" data-id="element-1234">
                    <stop offset="0%" stopColor="rgba(234,88,12,0.5)" data-id="element-1235" />
                    <stop offset="100%" stopColor="rgba(234,88,12,0)" data-id="element-1236" />
                  </linearGradient>
                </defs>
              </svg>

              {/* Center Logo — liquid-glass orange tile with a glossy top
               highlight so it reads as a polished, light-catching surface. */}
              <div className="relative z-20 w-16 h-16 rounded-2xl bg-gradient-to-b from-[#F97316] to-[#EA580C] flex items-center justify-center shadow-[0_0_50px_rgba(234,88,12,0.55),inset_0_2px_6px_rgba(255,255,255,0.5)] ring-1 ring-white/30 transform group-hover:scale-105 transition-transform duration-500 overflow-hidden" data-id="element-1237">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/35 to-transparent" data-id="element-1238" />
                <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="relative w-8 h-8 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1239" />
              </div>

              {/* Orbiting Icons — frosted liquid-glass tiles. */}
              <div className="absolute inset-0 z-10 flex items-center justify-center" data-id="element-1240">
                <div className="absolute top-[15%] left-[20%] w-11 h-11 rounded-xl bg-white/15 backdrop-blur-xl flex items-center justify-center shadow-[0_14px_28px_-6px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.4)] ring-1 ring-white/25 rotate-[-8deg] transform group-hover:-translate-y-2 group-hover:-translate-x-2 transition-transform duration-500" data-id="element-1241">
                  <BrandLogo brand="hubspot" className="w-5 h-5" data-id="element-1242" />
                </div>
                <div className="absolute bottom-[20%] left-[15%] w-11 h-11 rounded-xl bg-white/15 backdrop-blur-xl flex items-center justify-center shadow-[0_14px_28px_-6px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.4)] ring-1 ring-white/25 rotate-[5deg] transform group-hover:translate-y-2 group-hover:-translate-x-2 transition-transform duration-500" data-id="element-1243">
                  <BrandLogo brand="notion" className="w-5 h-5" data-id="element-1244" />
                </div>
                <div className="absolute top-[20%] right-[15%] w-11 h-11 rounded-xl bg-white/15 backdrop-blur-xl flex items-center justify-center shadow-[0_14px_28px_-6px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.4)] ring-1 ring-white/25 rotate-[12deg] transform group-hover:-translate-y-2 group-hover:translate-x-2 transition-transform duration-500" data-id="element-1245">
                  <BrandLogo brand="salesforce" className="w-5 h-5" data-id="element-1246" />
                </div>
                <div className="absolute bottom-[15%] right-[20%] w-11 h-11 rounded-xl bg-white/15 backdrop-blur-xl flex items-center justify-center shadow-[0_14px_28px_-6px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.4)] ring-1 ring-white/25 rotate-[-5deg] transform group-hover:translate-y-2 group-hover:translate-x-2 transition-transform duration-500" data-id="element-1247">
                  <BrandLogo brand="linear" className="w-5 h-5" data-id="element-1248" />
                </div>
              </div>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1249">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1250">
                Step 02
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-1251">
                Connect your tools
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1252">
                Link your CRM, Drive, calendar, and analytics. Each connection
                lets Giga act, not just read.
              </p>
            </div>
          </motion.div>

          {/* Card 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="group flex flex-col bg-white rounded-2xl md:rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1253">
            {/* Visual — instead of a big white chat panel, this reads as the
             moment a user is ABOUT to assign a job: a glassy Slack-style
             composer sitting on the brown stage with a half-typed "@Giga …"
             message and a live caret, so the brown background carries the
             scene rather than a dominant white surface. */}
            <div className="relative h-60 sm:h-64 bg-gradient-to-b from-[#1E1B26] to-[#0C0B10] flex items-center justify-center px-5 sm:px-6 overflow-hidden" data-id="element-1254">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(234,88,12,0.18),transparent_60%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1255" />
              {/* Dotted halftone pattern, anchored to the TOP of the dark header
               and faded out toward the mockup so it reads as texture, not
               noise behind the content. */}
              <div aria-hidden="true" className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent_75%)]" data-id="element-1256" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1257" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1258" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1259" />

              <div className="relative z-10 w-full max-w-[300px] flex flex-col gap-2.5" data-id="element-1260">
                {/* Channel context line — quietly grounds the composer in a
                 real workspace channel. */}
                <div className="flex items-center gap-1.5 text-white/40 text-[11px] font-semibold" data-id="element-1261">
                  <span className="text-white/30" data-id="element-1262">#</span>
                  operations
                </div>

                {/* Liquid-glass composer with a half-typed @Giga mention and a
                 blinking caret — "someone is about to assign a job". */}
                <div className="relative rounded-2xl bg-white/10 backdrop-blur-xl ring-1 ring-white/20 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.3)] p-3.5 transform group-hover:-translate-y-1 transition-transform duration-500" data-id="element-1263">
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-2xl bg-gradient-to-b from-white/15 to-transparent" data-id="element-1264" />
                  <p className="relative text-[13px] leading-relaxed text-white/90 font-medium" data-id="element-1265">
                    <span className="font-semibold text-[#FB923C]" data-id="element-1266">@Giga</span>{' '}
                    find us a cheaper packaging supplier
                    {/* Blinking caret */}
                    <motion.span aria-hidden="true" className="inline-block w-[2px] h-[1.05em] -mb-[0.15em] ml-0.5 bg-[#FB923C] align-middle" animate={{
                    opacity: [1, 1, 0, 0]
                  }} transition={{
                    duration: 1.1,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-1267" />
                  </p>

                  {/* Composer footer row — formatting affordances + a primed
                   send button, reinforcing "about to send". */}
                  <div className="relative mt-3 flex items-center justify-between" data-id="element-1268">
                    <div className="flex items-center gap-2 text-white/35" data-id="element-1269">
                      <span className="text-xs font-bold" data-id="element-1270">B</span>
                      <span className="text-xs italic font-semibold" data-id="element-1271">i</span>
                      <span className="text-xs font-mono" data-id="element-1272">{'</>'}</span>
                    </div>
                    <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-[#2EB67D] text-white shadow-[0_6px_16px_-4px_rgba(46,182,125,0.6)]" data-id="element-1273">
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 -ml-px" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-1274">
                        <path d="m22 2-7 20-4-9-9-4Z" data-id="element-1275" />
                        <path d="M22 2 11 13" data-id="element-1276" />
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Tiny helper line, like Slack's hint under the composer. */}
                <div className="flex items-center gap-1.5 text-white/30 text-[10px] font-medium" data-id="element-1277">
                  <span className="text-[#FB923C]" data-id="element-1278">↵</span>
                  Giga picks it up the moment you hit send
                </div>
              </div>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1279">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1280">
                Step 03
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-1281">
                Assign your first job
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1282">
                Give Giga a task that eats your week. It does it, then runs it
                on a schedule if you want.
              </p>
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <div ref={ctaRef} className="flex justify-center" data-id="element-1283">
          <WaitlistCTA className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#EA580C] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#EA580C]/20 transition-all duration-300 hover:bg-[#d24e09] hover:scale-105" data-id="element-1284">
            Get started for free
            <ArrowRight size={18} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-1285" />
          </WaitlistCTA>
        </div>
      </div>
    </section>;
}